import React, { useState, useRef, useContext, useLayoutEffect, useEffect } from "react";
import { useSelector } from "react-redux";
import DateLib from "../../../inc/DateLib";
import ApiProxy from "../../../app/api/admin/proxy/ApiProxy";
import Page from "../../modules/page";
import { ContextContent } from "../../modules/content/contentContext";
import InputWrapper from "../../components/input/__wrapper";
import InputRow from "../../components/input/__row";
import InputText from "../../components/inputs/text";
import InputNumber from "../../components/inputs/number/number";
import InputPassword from "../../components/inputs/password";
import InputCheckbox from "../../components/inputs/checkbox";
import InputDate from "../../components/inputs/date";
import Select from "../../components/inputs/select";
import { PROTOCOL_OPTIONS, formatProxyAddress, formatProxyExit } from "./proxy";
import {
    FAILURE_KINDS, PROXY_POLL_MS, ProxyCheckBadge, ProxyCheckButton, concurrencyTitle, formatConcurrency,
    formatStepOk, isProxyPending,
} from "./check";
import Button from "../../components/button";
import "./styles.css";

// Карточка прокси: show / edit / create одним компонентом (режим — contextContent.type).
// Поля — ProxyRequest / ProxyResource на бэкенде, дока — api/docs/API-FRONT-PROXIES.md.
export default function ProxyShow({ ...other }) {
    return <Page ApiModel={ApiProxy} {...other}>
        <ProxyShowContent/>
    </Page>;
}

function ProxyShowContent() {
    const contextContent = useContext(ContextContent);

    const [_id, set_id] = useState('');
    const [_host, set_host] = useState('');
    const [_port, set_port] = useState('');
    const [_protocol, set_protocol] = useState('socks5');
    const [_mode, set_mode] = useState(''); // '' — не определён (null): определит проверка
    const [_username, set_username] = useState('');
    const [_password, set_password] = useState('');
    const [_hasPassword, set_hasPassword] = useState(false);
    const [_isPasswordClear, set_isPasswordClear] = useState(false);
    const [_isActive, set_isActive] = useState(true);
    const [_expiresAt, set_expiresAt] = useState('');
    const [_proxyGroupId, set_proxyGroupId] = useState(''); // '' — без группы
    const [_exit, set_exit] = useState('—');
    const [_lastCheck, set_lastCheck] = useState(null);
    const [_concurrency, set_concurrency] = useState(null);

    // Пароль сервер не отдаёт, поле всегда стартует пустым: пустое = «не трогали», и diff его
    // не отправит. Стереть — отдельной галкой (ApiProxy.getParamsForUpdate → password: null).
    contextContent._dataToServer.current = {
        host: _host,
        port: _port,
        protocol: _protocol,
        // '' ↔ null: снятый режим уходит null (определит проверка); diff не шлёт не тронутый.
        mode: _mode === '' ? null : _mode,
        username: _username,
        password: _password,
        is_active: _isActive,
        expires_at: _expiresAt,
        // Select держит строку, '' — «Не выбрано». Шлём число или null: diff формы сравнивает
        // строками и null приравнивает к '', поэтому снятая группа уйдёт как null (не ''),
        // а не тронутая — не уйдёт вовсе.
        proxy_group_id: _proxyGroupId === '' ? null : Number(_proxyGroupId),
        ...(_isPasswordClear ? { is_password_clear: true } : {}),
    };

    const proxyGroups = useSelector((state) => state.option.proxyGroups);
    const deviceProxyModes = useSelector((state) => state.option.deviceProxyModes);

    const setData = (data) => {
        const groupId = data.proxy_group_id ?? null;
        set_id(data.id);
        set_host(data.host ?? '');
        set_port(data.port ?? '');
        set_protocol(data.protocol ?? 'socks5');
        set_mode(data.mode ?? '');
        set_username(data.username ?? '');
        set_password('');
        set_hasPassword(!!data.has_password);
        set_isPasswordClear(false);
        set_isActive(!!data.is_active);
        set_expiresAt(data.expires_at ?? '');
        set_proxyGroupId(groupId === null ? '' : String(groupId));
        set_exit(formatProxyExit(data));
        set_lastCheck(data.last_check ?? null);
        set_concurrency(data.concurrency ?? null);
        contextContent.setTitle(`Прокси: ${formatProxyAddress(data.host, data.port)}`);
    };

    useLayoutEffect(() => {
        contextContent._handleSetData.current = setData;
        if (contextContent.isCreate) contextContent.setTitle('Новый прокси');
        contextContent.doLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Режим на момент ОТВЕТА проверки, а не клика: пока она шла, могли перейти в edit.
    const isShowRef = useRef(true);
    isShowRef.current = contextContent.isShow;

    // Ответ «Проверить» / «Замерить» — прокси в КОРОТКОМ виде, как в списке: без разбора шагов
    // (last_check.steps, concurrency.runs) и дат. Накладываем его на текущую карточку, а не
    // заменяем — иначе «Подробнее» пропадали бы до следующего перечитывания. Если за время запроса
    // перешли в edit, поля формы не трогаем — иначе ответ молча стёр бы набранное.
    const handleCheckDone = (data) => {
        const previous = contextContent._dataFromServer.current ?? {};
        const merged = {
            ...previous,
            ...data,
            last_check: { ...(previous.last_check ?? {}), ...(data.last_check ?? {}) },
            concurrency: { ...(previous.concurrency ?? {}), ...(data.concurrency ?? {}) },
        };
        contextContent._dataFromServer.current = merged;
        if (isShowRef.current) {
            setData(merged);
            return;
        }
        set_exit(formatProxyExit(merged));
        set_lastCheck(merged.last_check);
        set_concurrency(merged.concurrency);
    };

    // Проверка из очереди или замер идут фоном — перечитываем карточку, пока не закончатся.
    // Только в show: в edit перечитывание затёрло бы набранное в форме.
    const isPending = isProxyPending({ last_check: _lastCheck, concurrency: _concurrency });
    const isShow = contextContent.isShow;
    const doLoad = contextContent.doLoad;
    useEffect(() => {
        if (!isPending || !isShow) return undefined;
        const timer = setInterval(doLoad, PROXY_POLL_MS);
        return () => clearInterval(timer);
    }, [isPending, isShow, doLoad]);

    const readOnly = !contextContent.isCreateOrEdit;
    const errors = contextContent.errors ?? {};

    return <>
        {!contextContent.isCreate ?
            <InputWrapper>
                <InputText className="input_wf" value={_id} readOnly={true}>id</InputText>
            </InputWrapper> : null}

        <InputRow>
            <InputWrapper>
                <InputText className="input_wf" value={_host} readOnly={readOnly} errors={errors.host}
                    placeholder="IP-адрес или имя, без схемы, логина и порта"
                    onChange={(e) => set_host(e.target.value)}>Адрес</InputText>
            </InputWrapper>
            <InputWrapper>
                <InputNumber className="input_wf" value={_port} readOnly={readOnly} errors={errors.port}
                    onChange={(e) => set_port(e.target.value)}>Порт</InputNumber>
            </InputWrapper>
            <InputWrapper>
                <Select className="input_wf" options={PROTOCOL_OPTIONS} value={_protocol} readOnly={readOnly}
                    errors={errors.protocol} onChange={(e) => set_protocol(e.target.value)}>Протокол</Select>
            </InputWrapper>
        </InputRow>

        <InputRow>
            <InputWrapper>
                <InputText className="input_wf" value={_username} readOnly={readOnly} errors={errors.username}
                    autoComplete="off" onChange={(e) => set_username(e.target.value)}>Логин</InputText>
            </InputWrapper>
            {contextContent.isCreateOrEdit ?
                <InputWrapper>
                    {!_isPasswordClear ?
                        <InputPassword className="input_wf" value={_password} errors={errors.password}
                            placeholder={(contextContent.isEdit && _hasPassword) ? 'Пусто — оставить прежний' : 'Пароль'}
                            autoComplete="new-password"
                            onChange={(e) => set_password(e.target.value)}>Пароль</InputPassword> : null}
                    {(contextContent.isEdit && _hasPassword) ?
                        <InputCheckbox value={_isPasswordClear} onChange={(e) => {
                            set_isPasswordClear(e.target.checked);
                            set_password('');
                        }}>Стереть пароль</InputCheckbox> : null}
                </InputWrapper> :
                <InputWrapper>
                    <InputText className="input_wf" value={_hasPassword ? 'задан' : 'не задан'} readOnly={true}>Пароль</InputText>
                </InputWrapper>}
        </InputRow>

        <InputRow>
            <InputWrapper>
                <InputDate className="input_wf" value={_expiresAt} readOnly={readOnly} errors={errors.expires_at}
                    onChange={(e) => set_expiresAt(e.target.value)}>Оплачен до</InputDate>
            </InputWrapper>
            <InputWrapper>
                <InputCheckbox value={_isActive} readOnly={readOnly}
                    onChange={(e) => set_isActive(e.target.checked)}>Активен</InputCheckbox>
            </InputWrapper>
        </InputRow>

        <InputRow>
            <InputWrapper>
                <Select className="input_wf" options={deviceProxyModes} value={_mode} isNull={true}
                    readOnly={readOnly} errors={errors.mode}
                    onChange={(e) => set_mode(e.target.value)}>Режим</Select>
                {!readOnly ? <div className="proxies__hint">
                    Не выбрано — режим определит проверка. Знаете, что UDP у прокси нет, — сразу «Только TCP».
                </div> : null}
                {(contextContent.isEdit && ((_mode || null) !== (contextContent._dataFromServer.current.mode ?? null)))
                    ? <div className="proxies__check-error">
                        Смена режима сбрасывает результат проверки.
                        {(_mode === 'tcp') ? ' Устройства с этим прокси в режиме «TCP + UDP» сами перейдут в «Только TCP» и переустановят прокси (5–15 мин).' : ''}
                    </div> : null}
            </InputWrapper>
            <InputWrapper>
                <Select className="input_wf" options={proxyGroups} value={_proxyGroupId} isNull={true}
                    readOnly={readOnly} errors={errors.proxy_group_id}
                    onChange={(e) => set_proxyGroupId(e.target.value)}>Группа</Select>
            </InputWrapper>
        </InputRow>

        {contextContent.isEdit ?
            <div className="proxies__hint">
                Смена адреса, порта, протокола, логина или пароля сбрасывает результат проверки —
                после сохранения проверьте прокси заново.
            </div> : null}

        {!contextContent.isCreate ?
            <ProxyCheckBlock
                proxyId={_id}
                lastCheck={_lastCheck}
                exit={_exit}
                canCheck={contextContent.checkPermission('edit')}
                isCheckDisabled={!contextContent.isShow}
                onDone={handleCheckDone}
            /> : null}

        {!contextContent.isCreate ?
            <ProxyConcurrencyBlock
                proxyId={_id}
                concurrency={_concurrency}
                canProbe={contextContent.checkPermission('edit')}
                isProbeDisabled={!contextContent.isShow}
                onDone={handleCheckDone}
            /> : null}
    </>;
}

// Результат последней проверки. Всё важное — в плоских полях last_check; steps — сырой разбор
// для «подробнее», логику на нём не строим.
// isCheckDisabled — в edit проверять нельзя: проверялся бы сохранённый адрес, а не набранный.
function ProxyCheckBlock({ proxyId, lastCheck, exit, canCheck, isCheckDisabled, onDone }) {
    const check = lastCheck ?? {};
    const values = [
        ['Выход', exit],
        ['Проверен', DateLib.formatterDateTime(check.checked_at) || '—'],
        ['TCP до хаба', formatStepOk(check.tcp_ok)],
        ['UDP до хаба', formatStepOk(check.udp_ok)],
        ['UDP под нагрузкой', (check.udp_load_ratio ?? null) === null ? '—' : `${Math.round(check.udp_load_ratio * 100)}%`],
        ['Пинг до прокси, мс', check.ping_ms ?? '—'],
        ['До хаба TCP, мс', check.latency_ms ?? '—'],
        ['До хаба UDP, мс', check.udp_latency_ms ?? '—'],
        ['Потеряно UDP-замеров', (check.udp_lost ?? null) === null ? '—' : `${check.udp_lost} из 9`],
    ];

    return <div className="proxies__check">
        <div className="proxies__check-title">Проверка</div>
        <div className="proxies__check-row">
            <ProxyCheckBadge status={check.status ?? null}/>
            {/* Уже в очереди или проверяется — второй раз не ставим (бэкенд и не поставит). */}
            {canCheck && proxyId !== ''
                ? <ProxyCheckButton proxyId={proxyId} onDone={onDone}
                    disabled={isCheckDisabled || isProxyPending({ last_check: check })}/> : null}
        </div>
        {check.error ? <div className="proxies__check-error">{check.error}</div> : null}
        {check.status === 'error' ? <div className="proxies__hint">Проверка не дошла до вердикта — повторите её.</div> : null}

        <div className="proxies__check-values">
            {values.map(([label, value]) => <div key={label} className="proxies__check-value">
                <span className="proxies__check-label">{label}</span>
                <span>{value}</span>
            </div>)}
        </div>

        {check.steps ?
            <details className="proxies__steps">
                <summary>Подробнее</summary>
                <pre>{JSON.stringify(check.steps, null, 2)}</pre>
            </details> : null}
    </div>;
}

// Замер одновременных соединений: потянет ли прокси устройство (потолок замера — 100). Справка —
// назначение на устройство им не ограничивается. Сам не запускается — только «Замерить».
function ProxyConcurrencyBlock({ proxyId, concurrency, canProbe, isProbeDisabled, onDone }) {
    const [isBusy, setIsBusy] = useState(false);
    const current = concurrency ?? {};
    const isPending = isProxyPending({ concurrency: current });

    const handleProbe = async () => {
        if (isBusy) return;
        setIsBusy(true);
        try {
            const data = await ApiProxy.Probe(proxyId).getResponse();
            if (data) onDone(data);
        } finally {
            setIsBusy(false);
        }
    };

    const failure = current.failed_at_step
        ? `${current.failed_at_step} соединений — ${FAILURE_KINDS[current.failure_kind] ?? current.failure_kind}`
        : (current.max !== null && current.max !== undefined ? 'прошёл все ступени' : '—');
    const values = [
        ['Замерен', DateLib.formatterDateTime(current.measured_at) || '—'],
        ['Отказал на', failure],
        ['Устройств на прокси при замере', current.assigned_devices ?? '—'],
    ];

    return <div className="proxies__check">
        <div className="proxies__check-title">Одновременных соединений</div>
        <div className="proxies__check-row">
            <span className="proxies__concurrency" title={concurrencyTitle(current)}>{formatConcurrency(current)}</span>
            {(canProbe && proxyId !== '')
                ? <Button disabled={isProbeDisabled || isPending || isBusy} onClick={handleProbe}>
                    {isBusy ? 'Ставлю в очередь…' : 'Замерить'}
                </Button> : null}
        </div>
        {(current.status === 'error' && current.error) ? <div className="proxies__check-error">{current.error}</div> : null}
        {current.assigned_devices > 0
            ? <div className="proxies__hint">На прокси уже сидели устройства — замерен остаток, а не потолок.</div>
            : null}
        {isPending ? <div className="proxies__hint">Замер идёт в очереди: около минуты, карточка обновится сама.</div> : null}

        <div className="proxies__check-values">
            {values.map(([label, value]) => <div key={label} className="proxies__check-value">
                <span className="proxies__check-label">{label}</span>
                <span>{value}</span>
            </div>)}
        </div>

        {current.runs ?
            <details className="proxies__steps">
                <summary>Подробнее</summary>
                <pre>{JSON.stringify(current.runs, null, 2)}</pre>
            </details> : null}
    </div>;
}
